import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Case } from '../../models/case';
import { CasesService } from '../../../services/cases.service';
import { caseTenantId } from '../../utils/case-display';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-case-details',
  templateUrl: './case-details.component.html',
  styleUrls: ['./case-details.component.scss']
})
export class CaseDetailsComponent implements OnInit {
  case: Case | null = null;
  loading = false;
  confirmingDelete = false;
  deleting = false;
  error: string | null = null;
  caseId: string | null = null;
  timelineItems: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private casesService: CasesService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.caseId = params.get('id');
      if (this.caseId) {
        this.loadCase();
      }
    });
  }

  loadCase(): void {
    if (!this.caseId) return;

    this.loading = true;
    this.error = null;

    this.casesService.getCaseById(this.caseId).subscribe({
      next: (caseData) => {
        this.case = caseData;
        this.loading = false;
        this.generateTimelineItems();
      },
      error: (error) => {
        console.error('Error loading case:', error);
        this.error = 'Failed to load case details';
        this.loading = false;
      }
    });
  }

  generateTimelineItems(): void {
    if (!this.case) return;
    
    this.timelineItems = [];
    
    // Add case creation
    if (this.case.createdAt) {
      this.timelineItems.push({
        type: 'case',
        title: 'Case Created',
        description: `Case #${this.case.id} was created`,
        status: this.case.status || 'Unknown',
        createdAt: this.case.createdAt
      });
    }
    
    // Add phases if they exist
    if (this.case.phases && this.case.phases.length > 0) {
      this.case.phases.forEach((phase, index) => {
        this.timelineItems.push({
          type: 'phase',
          title: `Phase ${index + 1}`,
          description: phase.description || 'No description',
          status: phase.status || 'Unknown',
          createdAt: phase.createdAt,
          flagged: phase.flagged
        });
      });
    }
    
    // Add shipments if they exist
    if (this.case.shipments && this.case.shipments.length > 0) {
      this.case.shipments.forEach((shipment, index) => {
        this.timelineItems.push({
          type: 'shipment',
          title: `Shipment ${index + 1}`,
          description: `Shipment from ${shipment.from?.city || 'Unknown'} to ${shipment.to?.city || 'Unknown'}`,
          status: shipment.status || 'Unknown',
          createdAt: shipment.createdAt,
          shipmentData: shipment
        });
      });
    }
    
    // Sort by creation date
    this.timelineItems.sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }

  askDelete(): void {
    if (!this.case?.id) return;

    this.confirmingDelete = true;
  }

  confirmDelete(): void {
    if (!this.case?.id) return;

    this.deleting = true;

    this.casesService.deleteCase(this.case.id, caseTenantId(this.case)).subscribe({
      next: () => {
        this.deleting = false;
        this.confirmingDelete = false;
        this.snackBar.open('Case deleted successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
        this.goBack();
      },
      error: (error) => {
        console.error('Error deleting case:', error);
        this.deleting = false;
        this.confirmingDelete = false;
        this.snackBar.open('Failed to delete case', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/partners']);
  }

  getPatientName(): string {
    return this.case?.patient?.name || 'N/A';
  }

  getPatientPhone(): string {
    return this.case?.patient?.phoneNo || 'N/A';
  }

  getCaseStatusClass(status: string): string {
    if (!status) return 'status-unknown';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('done')) {
      return 'status-completed';
    } else if (statusLower.includes('in progress') || statusLower.includes('active')) {
      return 'status-active';
    } else if (statusLower.includes('pending') || statusLower.includes('waiting')) {
      return 'status-pending';
    } else if (statusLower.includes('cancelled') || statusLower.includes('cancelled')) {
      return 'status-cancelled';
    }
    
    return 'status-unknown';
  }

  getDoctorName(): string {
    if (!this.case?.doctor?.contact?.name) return 'N/A';
    return this.case.doctor.contact.name;
  }

  getTechName(): string {
    if (!this.case?.tech?.contact?.name) return 'N/A';
    return this.case.tech.contact.name;
  }

  getTimelineItemIcon(type: string): string {
    switch (type) {
      case 'case': return 'folder';
      case 'phase': return 'assignment';
      case 'shipment': return 'local_shipping';
      default: return 'info';
    }
  }

  getTimelineItemStatusClass(status: string): string {
    return this.getCaseStatusClass(status);
  }

  isShipment(item: any): boolean {
    return item.type === 'shipment';
  }

  getShipmentData(item: any): any {
    return item.shipmentData || {};
  }
}
